/**
 * QrPaymentModal — QR-betaling (Mollie iDEAL) met polling op betaalstatus.
 *
 * WbModal 'dialoog': bedrag mono groot in geld-rood (open), QR in een vlak
 * met 2 px inktrand, "Controleer betaling" als hoofdactie onderin.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { salesforceClient } from '../../lib/salesforce';
import { PAYMENT, POLLING, TIMEOUTS } from '../../lib/constants';
import { WbButton, WbFlag, WbModal } from '../wb';

// =============================================================================
// TYPES
// =============================================================================

export interface QrPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  workOrderId: string;
  paymentUrl: string;
  openAmount: number;
  onPaymentReceived: (result: {
    totalTransactions: number;
    openAmount: number;
    isFullyPaid: boolean;
  }) => void;
  onRefreshRoute?: () => Promise<void>;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function QrPaymentModal({
  isOpen,
  onClose,
  workOrderId,
  paymentUrl,
  openAmount,
  onPaymentReceived,
  onRefreshRoute,
}: QrPaymentModalProps) {
  const [isPolling, setIsPolling] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [checkMessage, setCheckMessage] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const initialOpenAmountRef = useRef(openAmount);

  // Cleanup polling on unmount or close
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Start polling when modal opens
  useEffect(() => {
    if (isOpen) {
      initialOpenAmountRef.current = openAmount;
      startPolling();
    } else {
      stopPolling();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    setIsPolling(true);

    pollingIntervalRef.current = setInterval(async () => {
      if (!workOrderId) return;

      try {
        // Refresh route data to get latest payment info
        if (onRefreshRoute) {
          await onRefreshRoute();
        }
      } catch (err) {
        console.error('[QrPaymentModal] Error polling payment status:', err);
      }
    }, POLLING.PAYMENT_STATUS_MS);
  }, [workOrderId, onRefreshRoute]);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPolling(false);
  }, []);

  const handleClose = useCallback(() => {
    stopPolling();
    setCheckMessage(null);
    onClose();
  }, [stopPolling, onClose]);

  // Manual payment check (fallback for webhook)
  const handleCheckPayment = useCallback(async () => {
    if (!workOrderId) return;

    setIsChecking(true);
    setCheckMessage(null);

    try {
      const response = await salesforceClient.callApexRest<{
        success: boolean;
        data?: {
          status: string;
          message: string;
          totalTransactions: number;
          openAmount: number;
          isFullyPaid: boolean;
        };
        message?: string;
      }>(`/v1/field/workorders/${workOrderId}/check-payment`, 'POST');

      if (response.success && response.data) {
        const { status, message, totalTransactions, openAmount: newOpenAmount, isFullyPaid } = response.data;

        setCheckMessage(message);

        if (status === 'paid' || status === 'already_paid') {
          // Notify parent
          onPaymentReceived({
            totalTransactions: totalTransactions ?? 0,
            openAmount: newOpenAmount ?? 0,
            isFullyPaid: isFullyPaid ?? false,
          });

          // Haptic feedback
          if (navigator.vibrate) {
            navigator.vibrate([100, 50, 100]);
          }

          // Close modal after short delay
          setTimeout(() => {
            handleClose();
            if (onRefreshRoute) {
              onRefreshRoute();
            }
          }, TIMEOUTS.TOAST_DURATION_MS / 2);
        }
      } else {
        setCheckMessage(response.message || 'Fout bij controleren betaling');
      }
    } catch (err) {
      console.error('[QrPaymentModal] Error checking payment:', err);
      setCheckMessage('Kon betaling niet controleren');
    } finally {
      setIsChecking(false);
    }
  }, [workOrderId, handleClose, onRefreshRoute, onPaymentReceived]);

  if (!paymentUrl) return null;

  const betaald = !!checkMessage && (checkMessage.includes('ontvangen') || checkMessage.includes('betaald'));
  const mislukt = !!checkMessage && (checkMessage.includes('verlopen') || checkMessage.includes('mislukt'));

  return (
    <WbModal
      open={isOpen}
      onClose={handleClose}
      titel="QR-betaling"
      acties={
        <>
          <WbButton variant="secondary" size="lg" onClick={handleClose}>Sluiten</WbButton>
          <WbButton size="lg" onClick={handleCheckPayment} isLoading={isChecking}>Controleer betaling</WbButton>
        </>
      }
    >
      <div className="space-y-4">
        <WbFlag kind="geld" title="Nog te betalen">
          <span className="font-mono text-3xl font-semibold tabular-nums">€{openAmount.toFixed(2).replace('.', ',')}</span>
        </WbFlag>

        <div className="flex justify-center">
          <div className="p-3 bg-white border-wb border-wb-rand rounded-wb">
            <img
              src={`${PAYMENT.QR_CODE_API_URL}?text=${encodeURIComponent(paymentUrl)}&size=${PAYMENT.QR_CODE_SIZE_PX}`}
              alt="Mollie QR-code"
              className="w-48 h-48 object-contain"
            />
          </div>
        </div>

        <p className="text-sm text-wb-inkt2 text-center">
          Laat de klant de QR-code scannen met de camera of bank-app om te betalen via iDEAL.
        </p>

        {isPolling && !checkMessage && (
          <p className="font-mono text-sm text-wb-inkt2 text-center">Wachten op betaling…</p>
        )}

        {checkMessage && (
          <WbFlag kind={betaald ? 'info' : mislukt ? 'geld' : 'opmerking'} title={betaald ? 'Betaald' : undefined}>
            {checkMessage}
          </WbFlag>
        )}
      </div>
    </WbModal>
  );
}
